import { useState, useEffect } from 'react';
import { motion, AnimatePresence, useScroll } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ArrowRight } from 'lucide-react';

const navLinks = [
  { name: 'About', href: '#about', type: 'scroll' },
  { name: 'Services', href: '#services', type: 'scroll' },
  { name: 'Impact', href: '#impact', type: 'scroll' },
  { name: 'Blog', href: '/blog', type: 'link' },
  { name: 'Contact', href: '#contact', type: 'scroll' },
];

export function Navigation({ onOpenContact }) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { scrollYProgress } = useScroll();

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  const handleNavClick = (link) => {
    setIsMobileMenuOpen(false);
    if (link.type === 'link') {
      navigate(link.href);
      return;
    }
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.querySelector(link.href);
        if (element) element.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      const element = document.querySelector(link.href);
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      {/* Scroll progress bar */}
      <motion.div
        className="scroll-progress"
        style={{ scaleX: scrollYProgress, width: '100%' }}
        data-testid="scroll-progress"
      />

      <header
        className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between gap-4 px-6 md:px-10 py-4 md:py-5 bg-transparent"
        data-testid="main-navigation"
      >
        {/* Brand — logo + wordmark hard left */}
        <Link to="/" className="inline-flex items-center gap-2.5 group" data-testid="logo-link">
          <img
            src="/logo.png"
            alt="ServeNetics"
            className="h-9 w-auto rounded-md transition-transform duration-200 group-hover:scale-105"
          />
          <span className="hidden sm:inline font-heading font-semibold text-base text-stark tracking-tight">
            ServeNetics
          </span>
        </Link>

        {/* Pill nav — centered, each item carries its own soft chip */}
        <nav className="pill-nav hidden md:inline-flex" aria-label="Primary">
          {navLinks.map((link) => (
            <button
              key={link.name}
              onClick={() => handleNavClick(link)}
              className="pill-nav-item"
              data-testid={`nav-link-${link.name.toLowerCase()}`}
            >
              {link.name}
            </button>
          ))}
        </nav>

        {/* Right CTA — white pill */}
        <div className="flex items-center gap-2">
          <button
            onClick={onOpenContact}
            className="btn-light hidden md:inline-flex"
            data-testid="nav-book-consultation-btn"
          >
            Get Started
          </button>
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden grid place-items-center w-10 h-10 rounded-lg border border-white/15 bg-black/40 backdrop-blur text-stark"
            data-testid="mobile-menu-toggle"
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-x-3 top-[68px] z-40 p-5 rounded-2xl border border-white/15 bg-ember-deep/95 backdrop-blur-xl md:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col items-stretch gap-1 mb-4">
              {navLinks.map((link, index) => (
                <motion.button
                  key={link.name}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05, ease: [0.16, 1, 0.3, 1] }}
                  onClick={() => handleNavClick(link)}
                  className="text-left text-stark text-base py-3 px-3 rounded-lg hover:bg-white/5 transition-colors"
                  data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </motion.button>
              ))}
            </div>
            <button
              onClick={() => { setIsMobileMenuOpen(false); onOpenContact(); }}
              className="btn-flame w-full justify-center"
              data-testid="mobile-book-consultation-btn"
            >
              Get Started
              <span className="arrow-tile"><ArrowRight className="w-4 h-4" /></span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default Navigation;
